import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import {
  Figure,
  LadderHead,
  LadderRow,
  PageHeader,
  bacenCasesHref,
  useCodeLabel,
  useFormats,
} from '@/pages/bacen/shared';
import { fetchBacenPenalties } from '@/services/bacen';
import type { AnswerOut, BacenLadderTable } from '@/services/types';

/**
 * The fines: the tabulated amounts most rows carry, the first-instance and
 * board ladders, the same by year, office and subject type, and the
 * disqualification terms in years.
 *
 * Fine amounts here are what the ledger records at each stage, never what
 * was paid (the ledger carries no paid amount). Nothing forecasts the fine
 * on a pending case.
 */
export function BacenPenaltiesPage() {
  const { t } = useTranslation(['bacen', 'common']);
  const { number, currency, percent } = useFormats();
  const label = useCodeLabel();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['bacen', 'penalties'],
    queryFn: fetchBacenPenalties,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <PageHeader page="bacen:penalties" exportPath="/api/v1/bacen/export/penalties" />

      <AsOfStrip asOf={data.as_of} />

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-tabulation>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('bacen:penalties.tabulation.title')}
          </h2>
          <Answer answer={data.tabulation} help={t('bacen:penalties.tabulation.help')}>
            {(tab) => (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  {t('bacen:penalties.tabulation.summary', {
                    fines: number.format(tab.fines),
                    distinct: number.format(tab.distinct_amounts),
                    topShare: percent.format(tab.top_share),
                    top: number.format(tab.top.length),
                  })}
                </p>
                <ul className="divide-y rounded-md border text-sm">
                  {tab.top.map((row) => (
                    <li
                      key={row.amount}
                      className="flex items-center justify-between px-3 py-1.5"
                      data-amount={row.amount}
                    >
                      <span className="tabular-nums text-foreground">
                        {currency.format(row.amount)}
                      </span>
                      <span className="tabular-nums text-muted-foreground">
                        {number.format(row.rows)}
                        <span className="ml-1 text-xs">({percent.format(row.share)})</span>
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground" data-by-code>
                  {Object.entries(tab.by_penalty_code)
                    .map(([code, n]) => `${label('penalty', code)} ${number.format(n)}`)
                    .join(' · ')}
                </p>
              </div>
            )}
          </Answer>
          <div className="mt-3">
            <FieldGuide i18nKey="bacen:penalties.guide.tabulation" />
          </div>
        </section>

        <section className="rounded-lg border bg-card p-4" data-corpus-ladders>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('bacen:penalties.ladders.corpus.title')}
          </h2>
          <Answer answer={data.corpus_ladders} help={t('bacen:penalties.ladders.corpus.help')}>
            {(l) => (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <LadderHead nLabel={t('bacen:penalties.ladders.n')} />
                  <tbody>
                    <LadderRow
                      label={t('bacen:penalties.ladders.first')}
                      summary={l.first}
                      currency={currency}
                    />
                    <LadderRow
                      label={t('bacen:penalties.ladders.board')}
                      summary={l.board}
                      currency={currency}
                    />
                  </tbody>
                </table>
              </div>
            )}
          </Answer>
          <div className="mt-3">
            <FieldGuide i18nKey="bacen:penalties.guide.ladders" />
          </div>
        </section>
      </div>

      <section className="rounded-lg border bg-card p-4" data-terms>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:penalties.terms.title')}
        </h2>
        <Answer answer={data.disqualification_terms} help={t('bacen:penalties.terms.help')}>
          {(terms) => (
            <div className="space-y-3">
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Figure
                  label={t('bacen:penalties.terms.disqualifications')}
                  value={number.format(terms.disqualifications)}
                  href={bacenCasesHref({ penalty_code: 'DISQUALIFICATION' })}
                />
                <Figure
                  label={t('bacen:penalties.terms.withTerm')}
                  value={number.format(terms.with_term)}
                />
                <Figure
                  label={t('bacen:penalties.terms.persons')}
                  value={number.format(terms.persons)}
                />
                <Figure
                  label={t('bacen:penalties.terms.companies')}
                  value={number.format(terms.companies)}
                />
              </dl>
              {terms.years ? (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <LadderHead nLabel={t('bacen:penalties.ladders.n')} />
                    <tbody>
                      <LadderRow
                        label={t('bacen:penalties.terms.years')}
                        summary={terms.years}
                        currency={currency}
                        format={(v) => t('bacen:cases.years', { n: number.format(v) })}
                      />
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          )}
        </Answer>
      </section>

      <LadderTablePanel answer={data.by_year} />
      <LadderTablePanel answer={data.by_office} />
      <LadderTablePanel answer={data.by_subject} />
    </div>
  );
}

function LadderTablePanel({ answer }: { answer: AnswerOut<BacenLadderTable> }) {
  const { t } = useTranslation(['bacen']);
  const { number, currency, percent } = useFormats();
  const label = useCodeLabel();
  const kind = answer.value?.kind ?? 'year';
  const href = (group: string) =>
    kind === 'office'
      ? bacenCasesHref({ office: group })
      : kind === 'year'
        ? bacenCasesHref({ year: Number(group) })
        : bacenCasesHref({ subject_type: group });
  return (
    <section className="rounded-lg border bg-card p-4" data-ladder-table={kind}>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t(`bacen:penalties.ladders.${kind}.title`)}
      </h2>
      <Answer answer={answer} help={t(`bacen:penalties.ladders.${kind}.help`)}>
        {(table) => (
          <div className="space-y-2">
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">
                      {t(`bacen:penalties.ladders.${kind}.group`)}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:penalties.ladders.rows')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:penalties.ladders.firstMedian')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:penalties.ladders.firstP90')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:penalties.ladders.boardMedian')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:penalties.ladders.reduced')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row) => (
                    <tr key={row.group} className="border-t" data-group={row.group}>
                      <td className="px-3 py-2 text-foreground">
                        <Link
                          to={href(row.group)}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {kind === 'subject' ? label('subjectType', row.group) : row.group}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.rows)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {row.first ? currency.format(row.first.quantiles.p50 ?? 0) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {row.first ? currency.format(row.first.quantiles.p90 ?? 0) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {row.board ? currency.format(row.board.quantiles.p50 ?? 0) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.reduced)}
                        <span className="ml-1 text-xs">
                          ({percent.format(row.reduced_share_of_reviewed)})
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {table.suppressed > 0 ? (
              <p className="text-xs text-muted-foreground" data-suppressed>
                {t('bacen:penalties.ladders.suppressed', {
                  n: number.format(table.suppressed),
                })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
    </section>
  );
}
